import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
  MarkerType,
  type Edge,
  type Node,
  type Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Dagre from "@dagrejs/dagre";
import { AlertTriangle, ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { CampNode, type CampNodeData } from "./CampNode";
import { TensionEdge } from "./TensionEdge";
import { UnalignedPanel } from "./UnalignedPanel";
import { OutOfScopePanel, type OutOfScopePaper } from "./OutOfScopePanel";
import { CampDrillDownPanel, type CampPaperInfo } from "./CampDrillDownPanel";
import { DetectionBanner } from "./DetectionBanner";
import { toast } from "@/lib/toast";
import type { DebateMap } from "@/hooks/useFieldDebate";
import { useClaimActions } from "@/hooks/useClaims";
import { useDebateStore } from "@/stores/debate-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useFieldDebateGenerationStore } from "@/stores/field-debate-generation-store";
import {
  refreshErrorToastId,
  refreshLandedToastId,
  useFieldDebateRefreshLanded,
} from "@/hooks/useFieldDebateRefreshLanded";
import { applyCampScope, applyBridgesScope } from "@/lib/fieldDebateScope";
import { guideSignals } from "@/lib/guideSignals";

const nodeTypes = { campNode: CampNode };
const edgeTypes = { tension: TensionEdge };

// Dagre lays out with a fixed node box, so keep this roughly in sync with the
// real CampNode size. Height reserves room for the 3-line camp description
// (was 160 for the old 2-line clamp) so a taller card doesn't overlap the row
// below; ranksep adds a little extra breathing room on top of that.
const NODE_W = 300;
const NODE_H = 190;

/** "84s" under ~1.5 min, "9m 24s" above — status-bar durations. */
function fmtDuration(seconds: number): string {
  if (seconds < 100) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function getLayoutedElements(nodes: Node[], edges: Edge[]) {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 80, ranksep: 110 });

  for (const node of nodes) {
    g.setNode(node.id, { width: NODE_W, height: NODE_H });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  Dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const pos = g.node(node.id);
    return { ...node, position: { x: pos.x - NODE_W / 2, y: pos.y - NODE_H / 2 } };
  });

  return { nodes: layoutedNodes, edges };
}

interface Props {
  debateMap: DebateMap;
  conceptId: string;
  onCampClick?: (campId: string, paperIds: string[]) => void;
  onClaimClick?: (claimId: string) => void;
  compact?: boolean;
  readOnly?: boolean;
  /** Re-run generation after a failed batch build (partial_error). Wired to
   * the same generate action the toolbar uses; omitted on read-only surfaces. */
  onRetry?: () => void;
  /** Client-derived out-of-scope listing (current scores, score-desc — see
   * outOfScopePapers in useAnalyzeToolState). Omitted on surfaces without
   * paper data (shared route, compact embeds without the papers query). */
  outOfScopePapers?: OutOfScopePaper[];
  /** Title/score lookup for the camp drill-down panel (same displayPapers
   * derivation as outOfScopePapers). Omitted on surfaces without paper data —
   * the panel then falls back to paper-id stubs. */
  papers?: CampPaperInfo[];
}

function FieldDebateViewInner({ debateMap, conceptId, onCampClick, onClaimClick, compact, readOnly, onRetry, outOfScopePapers, papers }: Props) {
  const navigate = useNavigate();
  const savedViewport = useDebateStore((s) => s.viewport);
  const setViewport = useDebateStore((s) => s.setViewport);
  const selectClaim = useEvidenceStore((s) => s.selectClaim);
  const highlightedClaimId = useDebateStore((s) => s.highlightedClaimId);
  const highlightedCampId = useDebateStore((s) => s.highlightedCampId);
  const { createClaim } = useClaimActions(conceptId);
  const [blindSpotsOpen, setBlindSpotsOpen] = useState(true);
  const [blindSpotsCollapsed, setBlindSpotsCollapsed] = useState(false);
  const [expandedCamps, setExpandedCamps] = useState<Set<string>>(new Set());
  // Camp drill-down (trust-first legibility): full view only — compact embeds
  // have a fixed height and no paper data. Derived find() guards a stale id
  // after a rebuild swaps the camp set out from under the open panel.
  const [drillCampId, setDrillCampId] = useState<string | null>(null);
  const drillCamp =
    !compact && !readOnly && drillCampId
      ? debateMap.camps.find((c) => c.id === drillCampId) ?? null
      : null;

  // FD Stage 3: a batch REFRESH is in flight for this concept (map stays
  // complete/non-partial, so the partial-poll status never shows). Drives the
  // passive "Refreshing…" chip; useFieldDebate polls every 20s off the same
  // store flag until refresh_meta advances.
  const pendingRefresh = useFieldDebateGenerationStore((s) => s.pendingRefresh);
  const isRefreshing =
    (pendingRefresh?.conceptId === conceptId ||
      debateMap.pending_build?.kind === "refresh") &&
    !debateMap.partial;
  useFieldDebateRefreshLanded(conceptId, debateMap);

  // Clear the flag once a fetched map proves the refresh landed
  // (refresh_meta.refreshed_at newer than when we submitted), then toast the
  // audit count. getState() re-check guards double-toasts if two views of the
  // same map are mounted (analyze + compact embed).
  const refreshMeta = debateMap.refresh_meta;
  const refreshError = debateMap.refresh_error;
  useEffect(() => {
    if (!pendingRefresh || pendingRefresh.conceptId !== conceptId) return;
    // A failed batch refresh stamps refresh_error on the (still complete)
    // map — clear the chip and surface the error instead of polling forever.
    if (refreshError) {
      const store = useFieldDebateGenerationStore.getState();
      if (!store.pendingRefresh) return;
      store.setPendingRefresh(null);
      toast.error(refreshError, { id: refreshErrorToastId(conceptId) });
      return;
    }
    if (!refreshMeta?.refreshed_at) return;
    if (new Date(refreshMeta.refreshed_at).getTime() <= pendingRefresh.since) return;
    const store = useFieldDebateGenerationStore.getState();
    if (!store.pendingRefresh) return;
    store.setPendingRefresh(null);
    toast.success(
      `Map refreshed — ${refreshMeta.added} paper${refreshMeta.added === 1 ? "" : "s"} added.`,
      { id: refreshLandedToastId(conceptId, refreshMeta.refreshed_at) },
    );
  }, [pendingRefresh, conceptId, refreshMeta, refreshError]);

  const handleViewportChange = useCallback(
    (vp: Viewport) => setViewport(vp),
    [setViewport],
  );

  const handleCampClick = useCallback(
    (campId: string) => {
      if (readOnly) return;
      guideSignals.mark(conceptId, "hasDrilledIntoCamp");
      const camp = debateMap.camps.find((c) => c.id === campId);
      if (!camp) return;
      setDrillCampId(campId);
      if (onCampClick) {
        onCampClick(campId, camp.paper_ids);
      } else {
        applyCampScope(camp, conceptId);
      }
    },
    [debateMap.camps, conceptId, onCampClick, readOnly],
  );

  const handleClaimClick = useCallback(
    (claimId: string) => {
      if (readOnly) return;
      if (onClaimClick) {
        onClaimClick(claimId);
      } else {
        selectClaim(claimId);
        navigate({
          to: "/concepts/$conceptId/build",
          params: { conceptId },
        });
      }
    },
    [conceptId, navigate, selectClaim, onClaimClick, readOnly],
  );

  const { nodes, edges } = useMemo(() => {
    const campNodes: Node[] = debateMap.camps.map((camp, i) => ({
      id: camp.id,
      type: "campNode",
      position: { x: 0, y: 0 },
      data: {
        label: camp.label,
        description: camp.description,
        positionSummary: camp.position_summary,
        paperCount: (camp as any).paper_count ?? camp.paper_ids?.length ?? 0,
        topAuthors: "", // Could be enriched with paper data
        claims: camp.your_claims,
        colorIndex: i,
        onClaimClick: handleClaimClick,
        isHighlighted:
          camp.id === highlightedCampId ||
          (highlightedClaimId
            ? camp.your_claims?.some((c) => c.claim_id === highlightedClaimId)
            : false),
      } satisfies CampNodeData,
    }));

    const tensionEdges: Edge[] = debateMap.tensions.map((t, i) => {
      const campA = debateMap.camps.find((c) => c.id === t.camp_a);
      const campB = debateMap.camps.find((c) => c.id === t.camp_b);
      return {
        id: `tension-${i}`,
        source: t.camp_a,
        target: t.camp_b,
        type: "tension",
        data: {
          tensionType: t.tension_type,
          description: t.description,
          campAId: t.camp_a,
          campBId: t.camp_b,
          campALabel: campA?.label ?? t.camp_a,
          campBLabel: campB?.label ?? t.camp_b,
          campAPaperIds: campA?.paper_ids ?? [],
          campBPaperIds: campB?.paper_ids ?? [],
          conceptId,
          readOnly,
        },
        // Only `qualifies` is asymmetric (camp_a qualifies camp_b reads
        // differently in reverse) — show an arrowhead so the directionality
        // is legible. `contradicts` and `competes` are symmetric, so leave
        // them unmarked to avoid implying false directionality.
        ...(t.tension_type === "qualifies" && {
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: "#94e2d5",
            width: 16,
            height: 16,
          },
        }),
      };
    });

    return getLayoutedElements(campNodes, tensionEdges);
  }, [debateMap, handleClaimClick, highlightedClaimId, highlightedCampId]);

  const totalPapers = useMemo(() => {
    // Shared view uses paper_count; normal view sums paper_ids
    const byCount = debateMap.camps.reduce(
      (sum, camp) => sum + ((camp as any).paper_count ?? camp.paper_ids?.length ?? 0),
      0,
    );
    if (byCount > 0) return byCount;
    const seen = new Set<string>();
    for (const camp of debateMap.camps) {
      for (const pid of (camp.paper_ids ?? [])) seen.add(pid);
    }
    return seen.size;
  }, [debateMap.camps]);

  return (
    <div className={`relative flex-1 flex flex-col${compact ? " h-full" : ""}`}>
      {/* Shadow-detection annotation (spec 2026-07-13): full owner view
          only — compact embeds and the shared route never see it. It
          reframes how to read the whole map, so it sits above the canvas,
          not in the below-canvas panel stack. */}
      {!compact && !readOnly && debateMap.detection && (
        <DetectionBanner
          // Remount per detection record so the one-shot voted state resets
          // when a rebuild lands a new detection while mounted (Codex L1).
          key={debateMap.detection.detected_at}
          detection={debateMap.detection}
          conceptId={conceptId}
        />
      )}
      {/* Canvas — ReactFlow needs explicit height, not just flex-1. The wrapper
          is `relative` so canvas overlays (bridge badge, compact panel stack)
          anchor to the CANVAS edge, not the outer container — the container
          now grows below the canvas in full view (panel row + status bar). */}
      <div className="relative" style={compact ? { height: "100%" } : { height: 690 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={(_, node) => handleCampClick(node.id)}
          onViewportChange={handleViewportChange}
          defaultViewport={savedViewport ?? undefined}
          fitView={!savedViewport}
          proOptions={{ hideAttribution: true }}
        >
          {/* top-left: bottom-left belongs to the compact panel stack —
              controls there sat underneath it */}
          <Controls position="top-left" />
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        </ReactFlow>

        {/* Bridge papers badge — subtle, canvas bottom-right */}
        {debateMap.bridge_papers.length > 0 && (
          <div className="absolute bottom-3 right-3">
            <button
              type="button"
              className="text-[10px] text-muted-foreground/60 hover:text-foreground underline-offset-2 hover:underline"
              onClick={() => {
                if (readOnly) return;
                const ids = debateMap.bridge_papers.map((bp) => bp.paper_id);
                applyBridgesScope(ids, conceptId);
              }}
            >
              {debateMap.bridge_papers.length} bridge papers
            </button>
          </div>
        )}

        {/* Compact embeds only: out-of-scope / no-clear-stance stay as a
            bottom-left overlay — the compact card's height is fixed, so flow
            content below the canvas would overflow it. pointer-events-none
            wrapper keeps gaps click-transparent over the map. */}
        {compact &&
          (debateMap.scope ||
            (debateMap.unaligned && debateMap.unaligned.length > 0)) && (
            <div className="absolute bottom-3 left-3 flex flex-col gap-2 max-h-[calc(100%-4rem)] pointer-events-none">
              {debateMap.scope && (
              <OutOfScopePanel scope={debateMap.scope} papers={outOfScopePapers} />
            )}
              {debateMap.unaligned && debateMap.unaligned.length > 0 && (
                <UnalignedPanel unaligned={debateMap.unaligned} />
              )}
            </div>
          )}
      </div>

      {/* Full view: camp drill-down (why each paper landed there) +
          out-of-scope (never assessed) + no-clear-stance (assessed,
          abstained) flow BELOW the canvas as a wrapping row — overlaying them
          bottom-left crowded the map grid. */}
      {!compact &&
        (drillCamp ||
          debateMap.scope ||
          (debateMap.unaligned && debateMap.unaligned.length > 0)) && (
          <div className="mt-2 flex flex-wrap items-start gap-2">
            {drillCamp && (
              <CampDrillDownPanel
                key={drillCamp.id}
                camp={drillCamp}
                conceptId={conceptId}
                assignmentMeta={debateMap.assignment_meta}
                papers={papers}
                onClose={() => setDrillCampId(null)}
              />
            )}
            {debateMap.scope && (
              <OutOfScopePanel scope={debateMap.scope} papers={outOfScopePapers} />
            )}
            {debateMap.unaligned && debateMap.unaligned.length > 0 && (
              <UnalignedPanel unaligned={debateMap.unaligned} />
            )}
          </div>
        )}

      {/* Blind spots panel */}
      {debateMap.blind_spots.length > 0 && blindSpotsOpen && (
        <div className="absolute top-3 right-3 w-[22rem] max-h-[calc(100%-4rem)] flex flex-col bg-background/95 border border-border rounded-lg shadow-lg">
          <div className="flex items-center justify-between px-3 py-2 shrink-0">
            <button
              onClick={() => setBlindSpotsCollapsed((p) => !p)}
              className="flex items-center gap-1.5 text-yellow-400 text-xs font-semibold hover:text-yellow-300 transition-colors"
            >
              {blindSpotsCollapsed ? (
                <ChevronRight className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
              <AlertTriangle className="w-3.5 h-3.5" />
              Blind Spots ({debateMap.blind_spots.length})
            </button>
            <button
              onClick={() => setBlindSpotsOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          {!blindSpotsCollapsed && (<div className="overflow-y-auto min-h-0">
          <div className="space-y-2 px-3 pb-3">
            {debateMap.blind_spots.map((bs, i) => {
              const camp = debateMap.camps.find((c) => c.id === bs.camp_id);
              return (
                <div key={i} className="flex items-start gap-1 text-xs text-muted-foreground leading-snug">
                  <div className="flex-1">
                    <span className="font-medium text-foreground">
                      {camp?.label ?? bs.camp_id}:
                    </span>{" "}
                    {bs.gap}
                  </div>
                  {!readOnly && (
                    <button
                      onClick={() =>
                        createClaim.mutate(
                          { text: bs.gap, claim_type: "gap" },
                          {
                            onSuccess: () =>
                              toast.success("Gap claim created"),
                          },
                        )
                      }
                      className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground hover:text-purple-400 hover:bg-purple-500/20 transition-colors"
                      title="Seed as gap claim"
                    >
                      <Plus className="size-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Per-camp detailed blind spots (Pass 4) */}
          {debateMap.cluster_blind_spots && Object.keys(debateMap.cluster_blind_spots).length > 0 && (
            <>
              <div className="border-t border-border my-2" />
              <div className="text-[10px] text-muted-foreground/70 uppercase tracking-wide mb-1.5">
                Detailed per-camp analysis
              </div>
              <div className="space-y-1">
                {debateMap.camps.map((camp) => {
                  const spots = debateMap.cluster_blind_spots?.[camp.id];
                  if (!spots || spots.length === 0) return null;
                  const isExpanded = expandedCamps.has(camp.id);
                  return (
                    <div key={camp.id}>
                      <button
                        onClick={() =>
                          setExpandedCamps((prev) => {
                            const next = new Set(prev);
                            if (next.has(camp.id)) next.delete(camp.id);
                            else next.add(camp.id);
                            return next;
                          })
                        }
                        className="flex items-center gap-1 text-xs font-medium text-foreground hover:text-yellow-400 transition-colors w-full text-left"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3 shrink-0" />
                        ) : (
                          <ChevronRight className="w-3 h-3 shrink-0" />
                        )}
                        {camp.label}
                        <span className="text-muted-foreground/60 font-normal ml-1">
                          ({spots.length})
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="ml-4 mt-1 space-y-1.5">
                          {spots.map((spot, j) => (
                            <div key={j} className="flex items-start gap-1 text-xs text-muted-foreground leading-snug">
                              <span className="text-yellow-400/60 mt-0.5 shrink-0">&#x2022;</span>
                              <div className="flex-1">{spot}</div>
                              {!readOnly && (
                                <button
                                  onClick={() =>
                                    createClaim.mutate(
                                      { text: spot, claim_type: "gap" },
                                      {
                                        onSuccess: () =>
                                          toast.success("Gap claim created"),
                                      },
                                    )
                                  }
                                  className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground hover:text-purple-400 hover:bg-purple-500/20 transition-colors"
                                  title="Seed as gap claim"
                                >
                                  <Plus className="size-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>)}
        </div>
      )}

      {/* Reopen affordance — the panel's X dismisses it; this brings it back */}
      {debateMap.blind_spots.length > 0 && !blindSpotsOpen && (
        <button
          onClick={() => {
            setBlindSpotsOpen(true);
            setBlindSpotsCollapsed(false);
          }}
          className="absolute top-3 right-3 flex items-center gap-1.5 rounded-lg border border-border bg-background/95 px-2.5 py-1.5 text-xs font-semibold text-yellow-400 shadow-lg hover:text-yellow-300 transition-colors"
          title="Show blind spots"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          Blind Spots ({debateMap.blind_spots.length})
        </button>
      )}

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-t border-border text-[11px] text-muted-foreground">
        <span>
          {debateMap.camps.length} camps · {totalPapers} papers assigned ·{" "}
          {debateMap.bridge_papers.length} bridge papers
        </span>
        {debateMap.partial && !debateMap.partial_error ? (
          <span className="flex items-center gap-1.5 font-medium text-violet-400">
            {/* Violet = the card's RUNNING badge. bg-primary was near-black
                (light) / pale lavender (dark) and didn't read as activity. */}
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
            </span>
            {debateMap.head_elapsed_seconds != null
              ? `Top ${debateMap.head_paper_count ?? ""} placed in ${fmtDuration(debateMap.head_elapsed_seconds)} · assigning the rest…`
              : "Assigning papers…"}
          </span>
        ) : debateMap.partial && debateMap.partial_error ? (
          <span className="flex items-center gap-1.5 text-amber-500">
            {debateMap.partial_error}
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="underline underline-offset-2 hover:text-amber-400"
              >
                Retry
              </button>
            )}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            {isRefreshing && (
              <span className="flex items-center gap-1.5 font-medium text-sky-400">
                {/* Sky = the card's REFRESHING badge (same ping as the build dot). */}
                <span className="relative flex h-2 w-2" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500" />
                </span>
                Refreshing…
              </span>
            )}
            <span>
              {debateMap.refresh_meta?.refreshed_at
                ? `Refreshed ${new Date(debateMap.refresh_meta.refreshed_at).toLocaleString()}${
                    debateMap.camps_generated_at
                      ? ` · camps from ${new Date(debateMap.camps_generated_at).toLocaleDateString()}`
                      : ""
                  }`
                : `Generated ${new Date(debateMap.generated_at).toLocaleString()}`}
              {debateMap.head_elapsed_seconds != null &&
                ` · top ${debateMap.head_paper_count ?? ""} in ${fmtDuration(debateMap.head_elapsed_seconds)}`}
              {debateMap.elapsed_seconds != null &&
                ` · complete in ${fmtDuration(debateMap.elapsed_seconds)}`}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

export function FieldDebateView(props: Props) {
  return (
    <ReactFlowProvider>
      <FieldDebateViewInner {...props} />
    </ReactFlowProvider>
  );
}
